// ═══════════════════════════════════════════════════════════════════════
//   FASE 6 UI — Caixa + DRE + IA Financeira
//   Consome: /api/financial/caixa, /api/financial/dre, /api/financial/insights
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import { KpiCard, toneColor } from '@/design-system/dna';
import {
  Wallet, TrendingUp, Brain, Loader2, AlertTriangle, CheckCircle2,
  CreditCard, Layers, Sparkles, Users, Calendar, ArrowDownRight, ArrowUpRight,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API_URL = window.location.origin;

const fmtBRL = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = v => `${(v ?? 0).toFixed(2)}%`;

const COLORS = ['#7B3F98', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#3b82f6', '#ef4444', '#64748b'];

const KPICard = ({ icon, label, value, sub, color = 'violet', testid }) => (
  <KpiCard id={testid || String(label).toLowerCase().replace(/\s+/g, '-')} icon={icon} label={label} value={value} hint={sub} color={toneColor(color)} />
);

const Empty = ({ msg }) => (
  <div className="rounded-xl border-2 border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">
    {msg}
  </div>
);

const TableRow = ({ children, last }) => (
  <tr className={last ? '' : 'border-b border-slate-100'}>{children}</tr>
);

const fetchJson = async (path) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
};

// ═══════════════════════════════════════════════════════════════════════
//   ABA CAIXA
// ═══════════════════════════════════════════════════════════════════════
function CaixaTab({ filtros }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => v && params.append(k, v));
    setLoading(true); setError(null);
    fetchJson(`/api/financial/caixa?${params}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [filtros]);

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;
  if (error) return <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700">{error}</div>;
  if (!data) return <Empty msg="Sem dados" />;

  const r = data.resumo || {};
  const cats = data.por_categoria || [];
  const formas = data.por_forma_pagamento || [];
  const comps = data.por_competencia || [];
  const turmas = data.por_turma || [];

  return (
    <div className="space-y-4" data-testid="tab-caixa-content">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPICard icon={Wallet} label="Total Recebido" value={fmtBRL(r.total_pago)} sub={`${r.qtd_titulos || 0} títulos`} color="emerald" testid="kpi-total-pago" />
        <KPICard icon={Layers} label="Categorias" value={cats.length} sub="receitas distintas" color="violet" />
        <KPICard icon={CreditCard} label="Formas de Pgto" value={formas.length} sub="canais ativos" color="blue" />
      </div>

      {/* Por categoria + Por forma */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Por Categoria</h3>
          {cats.length === 0 ? <Empty msg="Sem recebimentos" /> : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={cats} layout="vertical" margin={{ left: 8 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => fmtBRL(v)} />
                  <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                    {cats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Forma de Pagamento</h3>
          {formas.length === 0 ? <Empty msg="Sem pagamentos" /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wider text-slate-500"><th className="text-left py-1.5">Forma</th><th className="text-right">Total</th></tr></thead>
              <tbody>
                {formas.map((f, i, arr) => (
                  <TableRow key={f.forma} last={i === arr.length - 1}>
                    <td className="py-2 capitalize text-slate-700">{f.forma.replace(/_/g, ' ')}</td>
                    <td className="py-2 text-right font-semibold text-slate-800">{fmtBRL(f.total)}</td>
                  </TableRow>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Por competência + Por turma */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Calendar className="w-4 h-4 text-violet-500" /> Por Competência</h3>
          {comps.length === 0 ? <Empty msg="Sem competências" /> : (
            <div className="space-y-1">
              {comps.map(c => (
                <div key={c.competencia} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm">
                  <span className="text-slate-600">{c.competencia}</span>
                  <span className="font-semibold text-slate-800">{fmtBRL(c.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Por Turma</h3>
          {turmas.length === 0 ? <Empty msg="Sem dados de turma" /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wider text-slate-500"><th className="text-left py-1.5">Turma</th><th className="text-right">Qtd</th><th className="text-right">Total</th></tr></thead>
              <tbody>
                {turmas.map((t, i, arr) => (
                  <TableRow key={t.id} last={i === arr.length - 1}>
                    <td className="py-2 text-slate-700">{t.name}</td>
                    <td className="py-2 text-right text-slate-500">{t.qtd}</td>
                    <td className="py-2 text-right font-semibold text-slate-800">{fmtBRL(t.total)}</td>
                  </TableRow>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//   ABA DRE
// ═══════════════════════════════════════════════════════════════════════
function DRETab({ filtros }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filtros.ano) params.append('ano', filtros.ano);
    if (filtros.unidade_id) params.append('unidade_id', filtros.unidade_id);
    setLoading(true); setError(null);
    fetchJson(`/api/financial/dre?${params}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [filtros]);

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;
  if (error) return <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700">{error}</div>;
  if (!data) return <Empty msg="Sem dados" />;

  return (
    <div className="space-y-4" data-testid="tab-dre-content">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPICard icon={ArrowUpRight} label="Receita Bruta" value={fmtBRL(data.receita_bruta)} sub={`${data.qtd_titulos} títulos`} color="emerald" testid="kpi-receita-bruta" />
        <KPICard icon={ArrowDownRight} label="Dedução ISS" value={fmtBRL(data.deducao_iss)} sub="impostos retidos" color="rose" testid="kpi-iss" />
        <KPICard icon={TrendingUp} label="Receita Líquida" value={fmtBRL(data.receita_liquida)} sub="após deduções" color="violet" testid="kpi-receita-liquida" />
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">Receitas por Categoria</h3>
        </div>
        {(data.linhas || []).length === 0 ? <Empty msg="Sem receitas no período" /> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-2">Linha DRE</th>
                <th className="text-right px-4 py-2">Qtd</th>
                <th className="text-right px-4 py-2">Bruto</th>
                <th className="text-right px-4 py-2">ISS</th>
                <th className="text-right px-4 py-2">Líquido</th>
              </tr>
            </thead>
            <tbody>
              {data.linhas.map((l, i) => (
                <tr key={l.linha} className={i === data.linhas.length - 1 ? '' : 'border-b border-slate-100'}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{l.linha}</td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{l.qtd}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">{fmtBRL(l.valor_bruto)}</td>
                  <td className="px-4 py-2.5 text-right text-rose-600">{fmtBRL(l.iss)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-emerald-700">{fmtBRL(l.valor_liquido)}</td>
                </tr>
              ))}
              <tr className="bg-violet-50 font-bold text-violet-800">
                <td className="px-4 py-3">TOTAL</td>
                <td className="px-4 py-3 text-right">{data.qtd_titulos}</td>
                <td className="px-4 py-3 text-right">{fmtBRL(data.receita_bruta)}</td>
                <td className="px-4 py-3 text-right">{fmtBRL(data.deducao_iss)}</td>
                <td className="px-4 py-3 text-right">{fmtBRL(data.receita_liquida)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//   ABA IA FINANCEIRA
// ═══════════════════════════════════════════════════════════════════════
function IATab({ filtros }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filtros.ano) params.append('ano', filtros.ano);
    setLoading(true); setError(null);
    fetchJson(`/api/financial/insights?${params}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [filtros]);

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;
  if (error) return <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700">{error}</div>;
  if (!data) return <Empty msg="Sem dados" />;

  const sr = data.score_risco_resumo || {};

  return (
    <div className="space-y-4" data-testid="tab-ia-content">
      {/* KPIs IA */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard icon={AlertTriangle} label="Alunos em Risco" value={data.alunos_em_risco?.length || 0} color="rose" testid="kpi-alunos-risco" />
        <KPICard icon={Users} label="Resp. Recorrentes" value={data.responsaveis_recorrentes_atraso?.length || 0} color="amber" />
        <KPICard icon={Brain} label="Score Médio Geral" value={`${sr.score_medio_geral ?? 0}/100`} sub={`${sr.qtd_score_alto ?? 0} score alto`} color="violet" />
        <KPICard icon={CheckCircle2} label="Alunos Analisados" value={sr.total_alunos_analisados || 0} color="blue" />
      </div>

      {/* Padrões */}
      {data.padroes_detectados?.length > 0 && (
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-violet-500" /> Padrões Detectados</h3>
          <ul className="space-y-1.5">
            {data.padroes_detectados.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className={`mt-1 w-2 h-2 rounded-full ${p.severidade === 'alta' ? 'bg-rose-500' : 'bg-amber-500'}`} />
                <span className="text-slate-700">{p.descricao}</span>
                <span className={`ml-auto text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${p.severidade === 'alta' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{p.severidade}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Alunos em risco */}
      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">Alunos com Maior Risco</h3>
        </div>
        {(data.alunos_em_risco || []).length === 0 ? <Empty msg="Nenhum aluno em risco" /> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-2">Aluno</th>
                <th className="text-right px-4 py-2">Vencidos</th>
                <th className="text-right px-4 py-2">Atraso médio</th>
                <th className="text-right px-4 py-2">Valor vencido</th>
                <th className="text-right px-4 py-2">Score</th>
              </tr>
            </thead>
            <tbody>
              {data.alunos_em_risco.map((a, i) => (
                <tr key={a.student_id} className={i === data.alunos_em_risco.length - 1 ? '' : 'border-b border-slate-100'}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{a.student_name || a.student_id}</td>
                  <td className="px-4 py-2.5 text-right">{a.qtd_vencidos}/{a.qtd_titulos}</td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{a.atraso_medio_dias}d</td>
                  <td className="px-4 py-2.5 text-right text-rose-600 font-semibold">{fmtBRL(a.valor_vencido)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md ${a.score_risco >= 70 ? 'bg-rose-100 text-rose-700' : a.score_risco >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                      {a.score_risco}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Inadimplência por categoria + Responsáveis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Inadimplência por Categoria</h3>
          {(data.inadimplencia_por_categoria || []).length === 0 ? <Empty msg="Sem dados" /> : (
            <div className="space-y-2">
              {data.inadimplencia_por_categoria.map(c => (
                <div key={c.categoria_id}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-700">{c.label}</span>
                    <span className={`font-semibold ${c.inadimplencia_pct >= 30 ? 'text-rose-600' : c.inadimplencia_pct >= 10 ? 'text-amber-600' : 'text-emerald-600'}`}>{fmtPct(c.inadimplencia_pct)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${c.inadimplencia_pct >= 30 ? 'bg-rose-500' : c.inadimplencia_pct >= 10 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(c.inadimplencia_pct, 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{fmtBRL(c.total_vencido)} de {fmtBRL(c.total_emitido)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Responsáveis Recorrentes</h3>
          {(data.responsaveis_recorrentes_atraso || []).length === 0 ? <Empty msg="Nenhum responsável em atraso" /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wider text-slate-500"><th className="text-left py-1.5">Responsável</th><th className="text-right">Vencidos</th><th className="text-right">Valor</th></tr></thead>
              <tbody>
                {data.responsaveis_recorrentes_atraso.map((r, i, arr) => (
                  <TableRow key={r.responsible_id} last={i === arr.length - 1}>
                    <td className="py-2 text-slate-700">{r.responsible_name || r.responsible_id?.slice(0, 8)}</td>
                    <td className="py-2 text-right text-slate-500">{r.qtd_vencidos}</td>
                    <td className="py-2 text-right text-rose-600 font-semibold">{fmtBRL(r.valor_vencido)}</td>
                  </TableRow>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Previsão de receita */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-violet-500" /> Previsão de Receita (próximos 3 meses)</h3>
        {(data.previsao_receita_proximos_3_meses || []).length === 0 ? (
          <Empty msg="Sem títulos abertos para os próximos 3 meses" />
        ) : (
          <div style={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <BarChart data={data.previsao_receita_proximos_3_meses}>
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => fmtBRL(v)} />
                <Bar dataKey="valor_esperado" fill="#7B3F98" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-400 text-center">
        Análise estatística determinística · {data.metadados?.total_titulos_analisados || 0} títulos · gerado em {data.gerado_em?.slice(0, 19)}
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//   PÁGINA PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════
export default function DRECaixaIA() {
  const currentYear = new Date().getFullYear();
  const [filtros, setFiltros] = useState({
    ano: currentYear,
    mes: '',
    unidade_id: '',
    class_group_id: '',
    categoria_id: '',
  });
  const [classGroups, setClassGroups] = useState([]);
  const [categorias, setCategorias] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    Promise.all([
      fetch(`${API_URL}/api/class-groups`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_URL}/api/financial/planos-turma/categorias/lista`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([cg, cat]) => {
      setClassGroups(Array.isArray(cg) ? cg : (cg.items || []));
      setCategorias(cat);
    });
  }, []);

  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const anos = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v === '__all__' ? '' : v }));

  return (
    <div className="space-y-5 p-6" data-testid="dre-caixa-ia-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Brain className="w-6 h-6 text-violet-600" />
            Caixa · DRE · IA Financeira
          </h1>
          <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1">
            <Sparkles className="w-3.5 h-3.5 text-violet-500" />
            FASE 6 · Motor Financeiro Central · agregação em tempo real sobre db.titles
          </p>
        </div>
      </div>

      {/* Filtros globais */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-[11px]">Ano</Label>
            <Select value={String(filtros.ano)} onValueChange={v => setF('ano', Number(v))}>
              <SelectTrigger data-testid="filter-ano"><SelectValue /></SelectTrigger>
              <SelectContent>{anos.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Mês (caixa)</Label>
            <Select value={filtros.mes ? String(filtros.mes) : '__all__'} onValueChange={v => setF('mes', v === '__all__' ? '' : Number(v))}>
              <SelectTrigger data-testid="filter-mes"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {meses.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Categoria</Label>
            <Select value={filtros.categoria_id || '__all__'} onValueChange={v => setF('categoria_id', v)}>
              <SelectTrigger data-testid="filter-categoria"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                {categorias.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Turma</Label>
            <Select value={filtros.class_group_id || '__all__'} onValueChange={v => setF('class_group_id', v)}>
              <SelectTrigger data-testid="filter-turma"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                {classGroups.map(cg => <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Unidade</Label>
            <Input
              placeholder="ID da unidade"
              value={filtros.unidade_id || ''}
              onChange={e => setF('unidade_id', e.target.value)}
              data-testid="filter-unidade"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="caixa" className="w-full">
        <TabsList className="grid grid-cols-3 w-full max-w-lg">
          <TabsTrigger value="caixa" data-testid="tab-caixa"><Wallet className="w-4 h-4 mr-1.5" /> Caixa</TabsTrigger>
          <TabsTrigger value="dre" data-testid="tab-dre"><TrendingUp className="w-4 h-4 mr-1.5" /> DRE</TabsTrigger>
          <TabsTrigger value="ia" data-testid="tab-ia"><Brain className="w-4 h-4 mr-1.5" /> IA Financeira</TabsTrigger>
        </TabsList>

        <TabsContent value="caixa" className="mt-4">
          <CaixaTab filtros={filtros} />
        </TabsContent>
        <TabsContent value="dre" className="mt-4">
          <DRETab filtros={{ ano: filtros.ano, unidade_id: filtros.unidade_id }} />
        </TabsContent>
        <TabsContent value="ia" className="mt-4">
          <IATab filtros={{ ano: filtros.ano }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
